import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { useLocation } from "react-router-dom";
import { NAV } from "../Navbar/navbarTheme";
import { sectionTitleSpacingSx } from "../sectionHeaderStyles";
import ContactChat from "../Hellper/ContactChat";
import WhatsAppIcon from "../Hellper/WhatsAppIcon";
import MapUK, {
  HEAD_OFFICE_GOOGLE_MAPS_URL,
} from "../Offices/MapOffices/MapUK";
import contactHeroImg from "../Images/ContactUs/Contact Us.jpg";
import {
  COUNTRY_CALLING_CODES,
  getCountryByIso,
  isoToFlagEmoji,
} from "../data/countryCallingCodes";

const WHATSAPP_GREEN = "#25D366";
const ACCENT_RED = "#c62828";
const CONTACT_RECIPIENT_EMAIL = "sales@uktomars.co.uk";
const CONTACT_MAILTO = `mailto:${CONTACT_RECIPIENT_EMAIL}`;
const FORM_SUBMIT_URL = `https://formsubmit.co/ajax/${CONTACT_RECIPIENT_EMAIL}`;

const LIMITS = {
  name: 60,
  email: 60,
  phoneLocal: 20,
  company: 60,
  message: 1000,
};

const allowedSimple = /^[a-zA-Z0-9 ]*$/;

const introParagraphs = [
  "Looking for a reliable UK wholesale and export supply partner?",
  "Our team supports trade buyers with product sourcing, competitive pricing, pallet supply, container loads, and export coordination.",
];

const pageContentSx = {
  px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
  py: { xs: 3.5, md: 5 },
  maxWidth: 1400,
  mx: "auto",
  width: "100%",
  boxSizing: "border-box",
};

const sectionHeadingSx = {
  color: NAV.ink,
  fontSize: { xs: "1.25rem", md: "1.5rem" },
  fontWeight: 700,
  lineHeight: 1.3,
  mb: { xs: 1.75, md: 2 },
};

const sectionBodySx = {
  color: NAV.ink,
  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
  lineHeight: 1.7,
  mb: 1.5,
};

const contactLinkSx = {
  color: NAV.navy,
  fontWeight: 600,
  fontSize: { xs: "0.9375rem", md: "1.0625rem" },
  textDecoration: "none",
  lineHeight: 1.6,
  "&:hover": { textDecoration: "underline", color: "#1e5a9e" },
};

const emailLinkTextSx = {
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  lineHeight: 1.45,
  fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9rem" },
  letterSpacing: "0.01em",
};

const enquiryMaxWidth = 1240;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#fff",
    fontSize: "0.9rem",
    "& fieldset": {
      borderColor: NAV.line,
    },
    "&:hover fieldset": {
      borderColor: "#c5d0dc",
    },
    "&.Mui-focused fieldset": {
      borderColor: NAV.navy,
      borderWidth: "1px",
    },
  },
};

const countrySelectSx = {
  ...fieldSx,
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    minHeight: 40,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 1,
  },
  "& .MuiSelect-icon": {
    color: NAV.inkMuted,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiSelect-icon": {
    color: NAV.navy,
  },
};

const countryMenuPaperSx = {
  maxHeight: 320,
  mt: 0.75,
  borderRadius: 2,
  border: `1px solid ${NAV.line}`,
  boxShadow: "0 12px 36px rgba(15, 40, 71, 0.14)",
  "& .MuiMenuItem-root": {
    py: 0.9,
    px: 1.5,
    borderRadius: 1.25,
    mx: 0.75,
    my: 0.2,
    fontSize: "0.9rem",
    "&:hover": { bgcolor: NAV.surfaceSubtle },
    "&.Mui-selected": {
      bgcolor: `${NAV.navy}10`,
      fontWeight: 600,
      "&:hover": { bgcolor: `${NAV.navy}16` },
    },
  },
};

const enquiryCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 2.5,
  border: `1px solid ${NAV.line}`,
  bgcolor: "#fff",
  boxShadow: "0 8px 24px rgba(15, 40, 71, 0.07)",
  overflow: "hidden",
};

function RequiredLabel({ children }) {
  return (
    <Typography
      component="label"
      sx={{
        display: "block",
        fontWeight: 600,
        color: NAV.ink,
        fontSize: { xs: "0.9rem", md: "0.9375rem" },
        mb: 0.75,
      }}
    >
      {children}{" "}
      <Box component="span" sx={{ color: ACCENT_RED }}>
        *
      </Box>
    </Typography>
  );
}

function OptionalLabel({ children }) {
  return (
    <Typography
      component="label"
      sx={{
        display: "block",
        fontWeight: 600,
        color: NAV.ink,
        fontSize: { xs: "0.9rem", md: "0.9375rem" },
        mb: 0.75,
      }}
    >
      {children}{" "}
      <Box
        component="span"
        sx={{ color: NAV.inkMuted, fontWeight: 500, fontSize: "0.82rem" }}
      >
        (optional)
      </Box>
    </Typography>
  );
}

function ContactLine({
  href,
  icon: Icon,
  iconColor = NAV.navy,
  children,
  external,
}) {
  return (
    <Box
      component="a"
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      sx={{
        ...contactLinkSx,
        display: "flex",
        alignItems: "center",
        gap: 1.1,
        mb: 1,
        "&:last-child": { mb: 0 },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: `${iconColor}14`,
          border: `1px solid ${iconColor}33`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: iconColor, fontSize: 18 }} />
      </Box>
      <Box
        component="span"
        sx={href?.startsWith("mailto:") ? emailLinkTextSx : undefined}
      >
        {children}
      </Box>
    </Box>
  );
}

function ContactDetailCard({ title, icon: Icon, children }) {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: { lg: 220 },
        display: "flex",
        flexDirection: "column",
        p: { xs: 2.25, md: 2.5, lg: 2.75 },
        borderRadius: 3,
        border: `1px solid ${NAV.line}`,
        bgcolor: "#fff",
        boxShadow: "0 8px 24px rgba(15, 40, 71, 0.08)",
        transition: "transform 0.22s ease, box-shadow 0.22s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 14px 32px rgba(15, 40, 71, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: { xs: 52, md: 56 },
            height: { xs: 52, md: 56 },
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${NAV.navy} 0%, #1e5a8a 100%)`,
            boxShadow: "0 10px 22px rgba(15, 40, 71, 0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: { xs: 26, md: 28 } }} />
        </Box>
        <Typography
          sx={{
            color: NAV.ink,
            fontWeight: 700,
            fontSize: { xs: "0.975rem", md: "1rem" },
            lineHeight: 1.35,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function ContactEnquiryForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryIso, setCountryIso] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const companyOk =
    !company.trim() ||
    (company.length <= LIMITS.company && allowedSimple.test(company));
  const messageOk =
    message.trim().length > 0 && message.length <= LIMITS.message;

  const resetForm = () => {
    setName("");
    setEmail("");
    setCountryIso("");
    setPhone("");
    setCompany("");
    setMessage("");
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const missingRequired =
      !name.trim() ||
      !email.trim() ||
      !phone.trim() ||
      !countryIso ||
      !message.trim();
    if (missingRequired) {
      setSubmitError(
        "Please fill in all required fields: Full Name, Email Address, Phone Number, Country, and Message.",
      );
      return;
    }
    if (!companyOk) {
      setSubmitError(
        "Company name can only contain letters, numbers, and spaces.",
      );
      return;
    }
    if (!messageOk) {
      setSubmitError(
        `Message must be between 1 and ${LIMITS.message} characters.`,
      );
      return;
    }

    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("email", email.trim());
    formData.append("phone", phone.trim());
    formData.append("company", company.trim() || "—");
    formData.append("country", getCountryByIso(countryIso)?.name || "");
    formData.append("message", message.trim());
    formData.append("_subject", `Contact enquiry from ${name.trim()}`);
    formData.append("_captcha", "false");
    formData.append("_template", "table");

    try {
      setIsSubmitting(true);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      let result = {};
      try {
        result = await response.json();
      } catch {
        /* non-JSON error response */
      }
      if (!response.ok || result?.success === false) {
        throw new Error("Unable to submit enquiry.");
      }

      resetForm();
      setSuccessOpen(true);
    } catch {
      setSubmitError(
        `Failed to send your enquiry. Please email ${CONTACT_RECIPIENT_EMAIL} directly.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={1.5}>
          <Box>
            <RequiredLabel>Full Name</RequiredLabel>
            <TextField
              fullWidth
              size="small"
              required
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, LIMITS.name))}
              inputProps={{ maxLength: LIMITS.name }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <RequiredLabel>Email Address</RequiredLabel>
            <TextField
              fullWidth
              size="small"
              required
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value.slice(0, LIMITS.email))}
              inputProps={{ maxLength: LIMITS.email }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon
                      sx={{ color: NAV.navy, opacity: 0.65, fontSize: 20 }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <RequiredLabel>Phone Number</RequiredLabel>
            <TextField
              fullWidth
              size="small"
              required
              placeholder="Phone number"
              value={phone}
              onChange={(e) => {
                const v = e.target.value
                  .replace(/[^\d\s-]/g, "")
                  .slice(0, LIMITS.phoneLocal);
                setPhone(v);
              }}
              inputProps={{ maxLength: LIMITS.phoneLocal }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <OptionalLabel>Company Name</OptionalLabel>
            <TextField
              fullWidth
              size="small"
              placeholder="Your company name"
              value={company}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= LIMITS.company && allowedSimple.test(v)) {
                  setCompany(v);
                }
              }}
              error={company.length > 0 && !companyOk}
              inputProps={{ maxLength: LIMITS.company }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <RequiredLabel>Select your country</RequiredLabel>
            <FormControl fullWidth size="small" required sx={countrySelectSx}>
              <Select
                displayEmpty
                required
                value={countryIso}
                onChange={(e) => setCountryIso(e.target.value)}
                inputProps={{ "aria-label": "Select your country" }}
                MenuProps={{ PaperProps: { sx: countryMenuPaperSx } }}
                renderValue={(iso) => {
                  const c = getCountryByIso(iso);
                  if (!c) {
                    return (
                      <Typography
                        component="span"
                        sx={{
                          color: NAV.inkMuted,
                          fontSize: "0.9rem",
                          fontStyle: "normal",
                        }}
                      >
                        Choose a country
                      </Typography>
                    );
                  }
                  return (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.2rem",
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                        aria-hidden
                      >
                        {isoToFlagEmoji(c.iso)}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          color: NAV.ink,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.name}
                      </Box>
                    </Stack>
                  );
                }}
              >
                {COUNTRY_CALLING_CODES.map((c) => (
                  <MenuItem key={c.iso} value={c.iso}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{ width: "100%" }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: "1.25rem",
                          width: 28,
                          textAlign: "center",
                          flexShrink: 0,
                        }}
                        aria-hidden
                      >
                        {isoToFlagEmoji(c.iso)}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: NAV.ink,
                          lineHeight: 1.35,
                        }}
                      >
                        {c.name}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <RequiredLabel>Message</RequiredLabel>
            <TextField
              fullWidth
              size="small"
              required
              multiline
              minRows={3}
              placeholder="How can we help you?"
              value={message}
              onChange={(e) => {
                const v = e.target.value;
                if (v.length <= LIMITS.message) setMessage(v);
              }}
              error={message.length > 0 && !messageOk}
              inputProps={{ maxLength: LIMITS.message }}
              sx={fieldSx}
            />
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
              {submitError}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="medium"
            disabled={isSubmitting}
            sx={{
              py: 1,
              borderRadius: 1.25,
              bgcolor: ACCENT_RED,
              fontWeight: 700,
              letterSpacing: "0.05em",
              fontSize: { xs: "0.85rem", md: "0.9rem" },
              boxShadow: "0 8px 22px rgba(198, 40, 40, 0.28)",
              "&:hover": { bgcolor: "#a61f1f" },
            }}
          >
            {isSubmitting ? "Sending…" : "SUBMIT"}
          </Button>
        </Stack>
      </Box>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: NAV.navy }}>
          Enquiry sent
        </DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
            <TaskAltRoundedIcon sx={{ fontSize: 52, color: "#2e7d32" }} />
            <Typography sx={{ textAlign: "center", color: NAV.inkMuted }}>
              Thank you. Your message has been sent to {CONTACT_RECIPIENT_EMAIL}
              . Our team will get back to you shortly.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{ bgcolor: NAV.navy, "&:hover": { bgcolor: "#163a5e" } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ContactUs() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box component="main" sx={{ width: "100%", bgcolor: "#FFF" }}>
      <Box
        component="header"
        sx={{
          width: "100%",
          bgcolor: "#FFF",
          borderBottom: `1px solid ${NAV.line}`,
        }}
      >
        <Box
          component="img"
          src={contactHeroImg}
          alt="Contact Us — UK TO MARS LTD wholesale and export support"
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            objectFit: "contain",
            objectPosition: "center top",
          }}
        />
      </Box>

      <Box sx={pageContentSx}>
        <Box
          component="section"
          aria-label="Introduction"
          sx={{
            mb: { xs: 3.5, md: 4.5 },
            pb: { xs: 3, md: 4 },
            borderBottom: `1px solid ${NAV.line}`,
          }}
        >
          <Typography component="h1" sx={sectionHeadingSx}>
            Let&apos;s Grow Your FMCG Business Together
          </Typography>
          {introParagraphs.map((text, index) => (
            <Typography
              key={text}
              sx={{
                ...sectionBodySx,
                mb: index < introParagraphs.length - 1 ? 2 : 0,
                maxWidth: 900,
              }}
            >
              {text}
            </Typography>
          ))}
        </Box>

        <Box
          component="section"
          aria-label="Contact details"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: { xs: 2.5, md: 2.75, lg: 3 },
            mb: { xs: 3.5, md: 4.5 },
            alignItems: "stretch",
          }}
        >
          <ContactDetailCard title="Email" icon={AlternateEmailOutlinedIcon}>
            <ContactLine
              href={CONTACT_MAILTO}
              icon={AlternateEmailOutlinedIcon}
            >
              {CONTACT_RECIPIENT_EMAIL}
            </ContactLine>
          </ContactDetailCard>

          <ContactDetailCard title="Phone" icon={PhoneInTalkOutlinedIcon}>
            <ContactLine href="tel:+441280733473" icon={PhoneOutlinedIcon}>
              +44 1280 733473
            </ContactLine>
            <ContactLine
              href="https://wa.me/447564627910"
              icon={WhatsAppIcon}
              iconColor={WHATSAPP_GREEN}
              external
            >
              +44 7564 627910
            </ContactLine>
          </ContactDetailCard>

          <ContactDetailCard title="Address" icon={LocationOnOutlinedIcon}>
            <Typography
              sx={{
                ...sectionBodySx,
                mb: 0,
                fontSize: { xs: "0.9rem", md: "0.975rem", lg: "0.9375rem" },
              }}
            >
              15 Little Balmer,
              <br />
              Buckingham Industrial Estate,
              <br />
              Buckingham, Buckinghamshire,
              <br />
              MK18 1TF, United Kingdom
            </Typography>
          </ContactDetailCard>

          <ContactDetailCard
            title="Business Opening Hours"
            icon={AccessTimeOutlinedIcon}
          >
            <Typography
              sx={{
                ...sectionBodySx,
                mb: 1,
                fontSize: { xs: "0.9rem", md: "0.975rem", lg: "0.9375rem" },
              }}
            >
              <strong>Monday – Friday:</strong> 9:00 AM – 5:00 PM
            </Typography>
            <Typography
              sx={{
                ...sectionBodySx,
                mb: 0,
                fontSize: { xs: "0.9rem", md: "0.975rem", lg: "0.9375rem" },
              }}
            >
              <strong>Saturday &amp; Sunday:</strong> Closed
            </Typography>
          </ContactDetailCard>
        </Box>
      </Box>

      <Box
        component="section"
        aria-label="Enquiry form and office location"
        sx={{
          width: "100%",
          bgcolor: "#FFF",
          borderTop: `1px solid ${NAV.line}`,
        }}
      >
        <Box
          sx={{
            width: "100%",
            bgcolor: ACCENT_RED,
            color: "#fff",
            py: { xs: 1.25, md: 1.5 },
            px: { xs: 2.5, sm: 3, md: 4 },
            boxShadow: "0 4px 14px rgba(198, 40, 40, 0.18)",
          }}
        >
          <Typography
            sx={{
              maxWidth: enquiryMaxWidth,
              mx: "auto",
              textAlign: "center",
              fontWeight: 700,
              fontSize: { xs: "0.875rem", sm: "0.9rem", md: "0.975rem" },
              lineHeight: 1.55,
              letterSpacing: "0.01em",
            }}
          >
            Our minimum wholesale order value is £3,000 excluding VAT (FCA,
            Incoterms 2020).
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: enquiryMaxWidth,
            mx: "auto",
            width: "100%",
            boxSizing: "border-box",
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              mb: { xs: 2.5, md: 3 },
              textAlign: { xs: "left", md: "center" },
            }}
          >
            <Typography
              component="h2"
              sx={{
                color: NAV.navy,
                fontWeight: 800,
                fontSize: { xs: "1.35rem", md: "1.65rem" },
                ...sectionTitleSpacingSx,
                lineHeight: 1.25,
                mb: 1,
              }}
            >
              Submit Your Enquiry
            </Typography>
            <Typography
              sx={{
                color: NAV.inkMuted,
                fontSize: { xs: "0.9rem", md: "1rem" },
                lineHeight: 1.6,
                maxWidth: 640,
                mx: { xs: 0, md: "auto" },
              }}
            >
              Get in touch with us today and we&apos;ll be happy to assist with
              your enquiry.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2.5, md: 3 },
              alignItems: "stretch",
            }}
          >
            <Box sx={{ ...enquiryCardSx, p: { xs: 2.25, md: 2.75 } }}>
              <ContactEnquiryForm />
            </Box>

            <Box sx={enquiryCardSx}>
              <Box
                component="a"
                href={HEAD_OFFICE_GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 1.5, md: 1.75 },
                  borderBottom: `1px solid ${NAV.line}`,
                  bgcolor: "#fff",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "background-color 0.2s ease",
                  "&:hover": { bgcolor: NAV.surfaceSubtle },
                }}
              >
                <LocationOnOutlinedIcon
                  sx={{ color: ACCENT_RED, fontSize: 22 }}
                />
                <Typography
                  sx={{
                    color: NAV.ink,
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", md: "0.975rem" },
                  }}
                >
                  UK Head Office
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: { xs: 280, md: 340 },
                  "& .leaflet-container": {
                    width: "100% !important",
                    height: "100% !important",
                    minHeight: { xs: 280, md: 340 },
                    margin: "0 !important",
                    borderRadius: 0,
                  },
                }}
              >
                <MapUK officeOnly embedded />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <ContactChat />
    </Box>
  );
}

export default ContactUs;
